'use client'
import { MultiCitySegmentWrapper } from '@/src/components/common/home/flightSearch/searchForm/components/DesktopMultiCitySearchForm/components/MultiCitySegmentWrapper'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import { getFormattedDate } from '@/src/utils/date'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import AddIcon from '@mui/icons-material/Add'
import { Box, Button, Menu, Typography } from '@mui/material'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { MultiCitySegment } from './hooks/search-inputs-manager'

interface MultiCityRouteSummaryProps {
    isVisible?: boolean
    active: boolean
    onActivate: () => void
    onClose: () => void
    segments: MultiCitySegment[]
    canAddMoreSegment: boolean
    handleMultiCitySegmentUpdate: (
        segmentIndex: number,
        field: 'from' | 'to' | 'date',
        value: string | Date | null
    ) => void
    handleMultiCitySwap: (segmentIndex: number) => void
    handleAddMultiCitySegment: () => void
    handleRemoveMultiCitySegment: (segmentIndex?: number) => void
    getAirportsForMultiCitySegment: (query: string, segmentIndex: number, field: 'from' | 'to') => void
}

/** Route editor panel; nested airport/calendar overlays use a higher z-index (1500). */
const MULTICITY_ROUTE_MENU_Z_INDEX = 1402


export const MultiCityRouteSummary: React.FC<MultiCityRouteSummaryProps> = ({
    isVisible = true,
    active,
    onActivate,
    onClose,
    segments,
    canAddMoreSegment,
    handleMultiCitySegmentUpdate,
    handleMultiCitySwap,
    handleAddMultiCitySegment,
    handleRemoveMultiCitySegment,
    getAirportsForMultiCitySegment
}) => {
    const t = useTranslations('common')
    const locale = useLocale()
    const { formatDate } = useFormatDate()
    const anchorRef = useRef<HTMLDivElement>(null)
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

    const [activeSegmentIndex, setActiveSegmentIndex] = useState<number | null>(null)
    const [activeSegmentField, setActiveSegmentField] = useState<'from' | 'to' | 'date' | null>(null)

    const directionArrow = '→'

    const segmentsWithSameAirportError = useMemo(() => {
        const errors = new Set<number>()
        segments.forEach((segment, index) => {
            const fromCode = segment.from.airportCode
            const toCode = segment.to.airportCode
            if (fromCode && toCode && fromCode === toCode) errors.add(index)
        })
        return errors
    }, [segments])

    const getMinDateForSegment = useCallback(
        (segmentIndex: number): Date | null => {
            if (segmentIndex === 0) return null
            for (let i = segmentIndex - 1; i >= 0; i--) {
                if (segments[i]?.date) return segments[i].date
            }
            return null
        },
        [segments]
    )

    const clearActiveState = useCallback(() => {
        setActiveSegmentIndex(null)
        setActiveSegmentField(null)
    }, [])

    useEffect(() => {
        if (active && !anchorEl && anchorRef.current) {
            setAnchorEl(anchorRef.current)
        } else if (!active) {
            setAnchorEl(null)
            clearActiveState()
        }
    }, [active, anchorEl, clearActiveState])

    useEffect(() => {
        if (!isVisible && active) {
            onClose()
            clearActiveState()
        }
    }, [isVisible, active, onClose, clearActiveState])

    const handleSegmentFieldActivation = useCallback((segmentIndex: number, field: 'from' | 'to' | 'date') => {
        setActiveSegmentIndex(segmentIndex)
        setActiveSegmentField(field)
    }, [])

    const isSegmentFieldActive = useCallback(
        (segmentIndex: number, field: 'from' | 'to' | 'date') =>
            activeSegmentIndex === segmentIndex && activeSegmentField === field,
        [activeSegmentIndex, activeSegmentField]
    )

    const isClickInsideNestedOverlay = useCallback((target: HTMLElement): boolean => {
        return !!(
            target.closest('.MuiMenu-root') ||
            target.closest('.MuiPopover-root') ||
            target.closest('.MuiModal-root') ||
            target.closest('.MuiPopper-root')
        )
    }, [])

    const createSegmentClickAwayHandler = useCallback(
        (segmentIndex: number) => (event: Event | React.SyntheticEvent) => {
            const target = event.target as HTMLElement
            if (isClickInsideNestedOverlay(target)) return
            if (activeSegmentIndex === segmentIndex) clearActiveState()
        },
        [activeSegmentIndex, isClickInsideNestedOverlay, clearActiveState]
    )

    const onAirportSelect = useCallback(
        (segmentIndex: number, value: string, action: 'from' | 'to') => {
            handleMultiCitySegmentUpdate(segmentIndex, action, value)
            clearActiveState()
        },
        [handleMultiCitySegmentUpdate, clearActiveState]
    )

    const onDateSelect = useCallback(
        (segmentIndex: number, dates: any) => {
            const selectedDate = dates?.from || null
            handleMultiCitySegmentUpdate(segmentIndex, 'date', selectedDate)
            clearActiveState()
        },
        [handleMultiCitySegmentUpdate, clearActiveState]
    )

    const onSwap = useCallback(
        (segmentIndex: number) => {
            clearActiveState()
            handleMultiCitySwap(segmentIndex)
        },
        [handleMultiCitySwap, clearActiveState]
    )

    const handleSummaryToggle = (event: React.MouseEvent<HTMLDivElement>) => {
        if (active) {
            setAnchorEl(null)
            onClose()
        } else {
            setAnchorEl(event.currentTarget)
            onActivate()
        }
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
        onClose()
        clearActiveState()
    }

    return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
                ref={anchorRef}
                onClick={handleSummaryToggle}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: active ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                    '&:hover': { bgcolor: active ? 'rgba(25, 118, 210, 0.12)' : 'action.hover' }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, overflow: 'hidden' }}>
                    {segments.length ? segments.map((segment, i) => {
                        const from = segment.from?.airportCode || ''
                        const to = segment.to?.airportCode || ''
                        if (!from && !to) return null
                        const date = segment.date
                            ? formatDate(getFormattedDate(segment.date, 'MM/DD/YYYY'), 'DD MMM')
                            : ''
                        return (
                            <Box
                                key={i}
                                sx={{
                                    px: 1.25,
                                    py: 0.5,
                                    borderRadius: 1.5,
                                    bgcolor: active ? 'primary.main' : 'grey.100',
                                    border: '1px solid',
                                    borderColor: active ? 'primary.main' : 'grey.300',
                                    flexShrink: 0
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: active ? 'primary.contrastText' : 'text.primary', lineHeight: 1.3 }}
                                >
                                    {from} {directionArrow} {to}
                                </Typography>
                                {date ? (
                                    <Typography
                                        variant="caption"
                                        sx={{ color: active ? 'primary.contrastText' : 'text.secondary', opacity: active ? 0.85 : 1, lineHeight: 1.3 }}
                                    >
                                        {date}
                                    </Typography>
                                ) : null}
                            </Box>
                        )
                    }) : (
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {t('multicity')}
                        </Typography>
                    )}
                </Box>
                {active ? <ExpandLess sx={{ color: 'grey.500', flexShrink: 0 }} /> : <ExpandMore sx={{ color: 'grey.500', flexShrink: 0 }} />}
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={active && Boolean(anchorEl)}
                onClose={handleMenuClose}
                keepMounted
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    root: {
                        disableEnforceFocus: true
                    },
                    list: {
                        component: 'div',
                        sx: { p: 0 }
                    },
                    paper: {
                        sx: {
                            mt: 1,
                            p: 2,
                            width: 720,
                            maxWidth: 'none',
                            maxHeight: '70vh',
                            overflowY: 'auto'
                        }
                    }
                }}
                sx={{ zIndex: MULTICITY_ROUTE_MENU_Z_INDEX }}
            >
                <Box>
                    {segments.map((segment, index) => (
                        <MultiCitySegmentWrapper
                            key={index}
                            segment={segment}
                            segmentIndex={index}
                            segmentsLength={segments.length}
                            segmentsWithSameAirportError={segmentsWithSameAirportError}
                            isSegmentFieldActive={isSegmentFieldActive}
                            onSegmentFieldActivation={handleSegmentFieldActivation}
                            onAirportSelect={onAirportSelect}
                            onDateSelect={(segmentIndex, dates) => onDateSelect(segmentIndex, dates)}
                            onSwap={onSwap}
                            onRemove={handleRemoveMultiCitySegment}
                            getAirportsForSegment={getAirportsForMultiCitySegment}
                            closeActiveSegmentComponent={clearActiveState}
                            createSegmentClickAwayHandler={createSegmentClickAwayHandler}
                            getMinDateForSegment={getMinDateForSegment}
                        />
                    ))}

                    {canAddMoreSegment && (
                        <Button
                            onClick={handleAddMultiCitySegment}
                            variant="outlined"
                            size="medium"
                            startIcon={<AddIcon />}
                            sx={{ py: 1, px: 3, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                        >
                            {t('addMore')}
                        </Button>
                    )}
                </Box>
            </Menu>
        </Box>
    )
}

export default MultiCityRouteSummary
